import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, SearchIcon, XIcon } from 'lucide-react'
import { useMemo } from 'react'
import { ScreenHeader } from '@app/components/screen-header'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { Card } from '@app/components/ui/card'
import { Input } from '@app/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNumCell,
  TableRow,
  TableScroller,
} from '@app/components/ui/table'
import { cn } from '@app/lib/cn'
import { useCsvDownload } from '@app/lib/download'
import { useUrlSearchInput } from '@app/lib/nav'
import type { CariTipParam } from '@app/router'
import { cariApi, cariQueryKeys } from '@app/features/cari/api'
import type { CariCurrencyTotal, CariFilter, CariListResponse } from '@app/features/cari/api'
import { formatAmount, formatDate, formatQty } from '@app/features/cari/format'
import {
  BalanceStack,
  CariTypeBadge,
  EmptyState,
  ErrorState,
  TableSkeleton,
} from '@app/features/cari/ui'

/**
 * Cari list — the owners' "who owes what" screen.
 *
 * Korgun answers this question with a report you configure first and read
 * afterwards, per currency, per location, in a separate window. Here every
 * account is on screen the moment the page opens, search is live, and the
 * receivable/payable strip at the top recomputes with the filters.
 *
 * The one thing this screen deliberately does NOT do is add currencies
 * together. Korgun never converts, and several suppliers are invoiced in USD
 * but paid in TL — their true position only makes sense as two numbers.
 */

const route = getRouteApi('/_korumali/cari')

const PAGE_SIZE = 50

/**
 * Account type. "Diğer" is the 335/360 bucket — payroll and the handful of
 * accounts that are neither customer nor supplier. It used to be a dead count
 * printed after the chips ("+ 31 personel / diğer hesap"), which read as a chip
 * but did nothing when clicked; it is a real filter now.
 */
const TYPE_CHIPS: { value: CariTipParam; label: string; key: keyof CariListResponse['summary'] }[] = [
  { value: 'all', label: 'Tümü', key: 'all' },
  { value: 'customer', label: 'Müşteri', key: 'customer' },
  { value: 'supplier', label: 'Tedarikçi', key: 'supplier' },
  { value: 'other', label: 'Diğer', key: 'other' },
]

/**
 * Column priority. The sidebar leaves the table ~570px at 834 and ~936px at
 * 1200: code, name, Bakiye and the last movement date are the screen, so they
 * never leave. Tip is derivable from the code's 120/320 prefix (and is what the
 * chips filter on anyway), so it goes first; Şehir is reference data.
 */
const COLUMN = {
  type: 'hidden lg:table-cell',
  city: 'hidden min-[1140px]:table-cell',
} as const

export function CariScreen() {
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const csv = useCsvDownload()

  const query = search.query ?? ''
  const tip = search.tip ?? 'all'
  const page = search.page ?? 1

  /*
   * The box keeps its own copy so typing never waits for a navigation, and
   * lands in the URL 300 ms later — `replace`, so it stays one history entry.
   */
  const [searchInput, setSearchInput, flushSearch] = useUrlSearchInput(query, (text) => {
    void navigate({
      search: (prev) => ({ ...prev, query: text === '' ? undefined : text, page: undefined }),
      replace: true,
      // The commit fires mid-typing — a scroll reset would yank the viewport
      // away from the box (see the üretim list for the full story).
      resetScroll: false,
    })
  })

  const params = useMemo(
    () => ({
      query,
      /*
       * `other` = the 335/360 accounts. `TYPE_SQL` in the cari API already
       * labels them, and the summary counts them (31 on the mirror), which is
       * where the chip's number comes from.
       *
       * Server side: the Worker's FILTERS accepts 'other' (list + export).
       */
      tip: tip as CariFilter,
      page,
      pageSize: PAGE_SIZE,
    }),
    [query, tip, page],
  )

  const { data, isPending, isError, error, isFetching, refetch } = useQuery({
    queryKey: cariQueryKeys.list(params),
    queryFn: () => cariApi.list(params),
    placeholderData: keepPreviousData,
  })

  const rows = data?.rows ?? []
  const summary = data?.summary
  const totals = data?.totals ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1
  // With `keepPreviousData` the response still holds the OLD page while the next
  // one loads, so the range label is read from the response, not local state.
  const serverPage = data?.page ?? page
  const serverPageSize = data?.pageSize ?? PAGE_SIZE
  const first = total === 0 ? 0 : (serverPage - 1) * serverPageSize + 1
  const last = Math.min(serverPage * serverPageSize, total)
  const hasFilters = query !== '' || tip !== 'all'

  /** The URL is the single writer; the search box follows it. */
  function clearFilters() {
    void navigate({
      search: (prev) => ({ ...prev, query: undefined, tip: undefined, page: undefined }),
      resetScroll: false,
    })
  }

  /** Page 1 is the default, so it leaves the URL entirely. */
  function goToPage(next: number) {
    void navigate({ search: (prev) => ({ ...prev, page: next <= 1 ? undefined : next }) })
  }

  return (
    <>
      <ScreenHeader
        title="Cari"
        description="Müşteri ve tedarikçi hesapları — bakiye, ekstre ve hareket geçmişi."
        actions={
          <span className="font-mono text-micro tnum text-ink-muted">
            {isPending ? 'yükleniyor…' : `${formatQty(total)} kart`}
          </span>
        }
      />

      {/* ── Summary strip: receivable vs payable, per currency ── */}
      <TotalsStrip totals={totals} loading={isPending} />

      {/*
       * The legend belongs HERE, not under the table: the two colours are first
       * used by the tiles above, and a reader who has to scroll past 50 rows to
       * find out what mustard means has already guessed wrong.
       */}
      <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-micro text-ink-muted">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2 rounded-xs bg-zeytin" aria-hidden />
          <span className="text-zeytin">Zeytin</span> = cari bize borçlu
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2 rounded-xs bg-hardal" aria-hidden />
          <span className="text-hardal">Hardal</span> = biz cariye borçluyuz
        </span>
      </p>

      <Card className="mt-3">
        <div className="flex flex-col gap-2.5 border-b border-hairline px-3.5 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-52 flex-1 sm:max-w-xs">
              <SearchIcon
                className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-ink-muted"
                aria-hidden
              />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Ünvan, cari kodu ya da vergi no"
                aria-label="Cari kartlarda ara"
                className="pl-7"
              />
              {searchInput ? (
                <button
                  type="button"
                  onClick={() => setSearchInput('')}
                  aria-label="Aramayı temizle"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-xs p-0.5 text-ink-muted hover:text-ink"
                >
                  <XIcon className="size-3.5" aria-hidden />
                </button>
              ) : null}
            </div>

            {/* Exports every filtered account, one row per account and currency. */}
            <Button
              variant="outline"
              disabled={csv.busy}
              aria-busy={csv.busy}
              onClick={() => csv.start('/cari/export', { query: flushSearch(), tip })}
            >
              <DownloadIcon aria-hidden />
              {csv.busy ? 'Hazırlanıyor…' : 'Dışa aktar'}
            </Button>

            {csv.error ? (
              <span role="alert" className="text-micro text-hardal">
                {csv.error}
              </span>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-1">
            {TYPE_CHIPS.map((chip) => {
              const active = tip === chip.value
              const count = summary?.[chip.key]
              return (
                <button
                  key={chip.value}
                  type="button"
                  onClick={() => {
                    // Keeps the viewport still and carries text waiting on the
                    // search debounce (see the üretim list).
                    const text = flushSearch({ commit: false })
                    void navigate({
                      search: (prev) => ({
                        ...prev,
                        query: text === '' ? undefined : text,
                        tip: chip.value === 'all' ? undefined : chip.value,
                        page: undefined,
                      }),
                      resetScroll: false,
                    })
                  }}
                  aria-pressed={active}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-xs border px-2 py-1 text-micro transition-colors',
                    active
                      ? 'border-kordovan/30 bg-kordovan/8 font-medium text-kordovan'
                      : 'border-hairline bg-surface text-ink-muted hover:border-ink-muted/40 hover:text-ink',
                  )}
                >
                  {chip.label}
                  <span
                    className={cn('font-mono tnum', active ? 'text-kordovan' : 'text-ink-muted/70')}
                  >
                    {count === undefined ? '·' : formatQty(count)}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        <TableScroller className={cn(isFetching && !isPending && 'opacity-60 transition-opacity')}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Kod</TableHead>
                <TableHead className="min-w-40">Ünvan</TableHead>
                <TableHead className={cn('w-28', COLUMN.type)}>Tip</TableHead>
                <TableHead className={cn('w-32', COLUMN.city)}>Şehir</TableHead>
                <TableHead className="w-44 text-right">Bakiye</TableHead>
                <TableHead className="w-28">Son işlem</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isPending ? (
                <TableSkeleton rows={12} cols={6} />
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <ErrorState
                      message={
                        error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu.'
                      }
                      onRetry={() => void refetch()}
                    />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <EmptyState
                      title="Bu filtreyle cari kartı yok"
                      description={
                        hasFilters
                          ? 'Aramayı kısaltıp ya da tip filtresini kaldırıp yeniden bakın.'
                          : 'Cari kartı listesi boş görünüyor. Korgun aynası güncel mi kontrol edin.'
                      }
                      action={
                        hasFilters ? (
                          <Button variant="primary" onClick={clearFilters}>
                            Filtreleri temizle
                          </Button>
                        ) : null
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.ckod} className="relative">
                    <TableCell className="font-mono">
                      {/* The whole row is the link — no record arrows like Korgun. */}
                      <Link
                        to="/cari/$ckod"
                        params={{ ckod: row.ckod }}
                        className="font-medium text-ink after:absolute after:inset-0 after:content-[''] hover:text-kordovan"
                      >
                        {row.ckod}
                      </Link>
                    </TableCell>

                    {/* max-w-0 lets the column give way: `truncate` sets white-space
                        to nowrap, which would otherwise pin the column to the widest
                        ünvan in the page and push the table into scroll. */}
                    <TableCell className="max-w-0">
                      <span className="flex items-center gap-1.5">
                        <span className="min-w-0 truncate font-medium text-ink">{row.name}</span>
                        {row.inactive ? <Badge tone="neutral">Pasif</Badge> : null}
                        {row.overseas ? <Badge tone="info">Yurt dışı</Badge> : null}
                      </span>
                      {row.title && row.title !== row.name ? (
                        <span className="block truncate text-micro text-ink-muted">{row.title}</span>
                      ) : null}
                    </TableCell>

                    <TableCell className={COLUMN.type}>
                      <CariTypeBadge type={row.type} typeName={row.typeName} />
                    </TableCell>

                    <TableCell className={cn('truncate text-ink-muted', COLUMN.city)}>
                      {row.city ?? row.country ?? '—'}
                    </TableCell>

                    <TableNumCell>
                      <BalanceStack balances={row.balances} />
                    </TableNumCell>

                    <TableCell className="font-mono tnum text-ink-muted">
                      {formatDate(row.lastActivity)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableScroller>

        {!isPending && !isError && rows.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-hairline px-3.5 py-2">
            <span className="font-mono text-micro tnum text-ink-muted">
              {formatQty(first)}–{formatQty(last)} / {formatQty(total)}
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >
                <ChevronLeftIcon aria-hidden />
                Önceki
              </Button>
              <span className="px-1 font-mono text-micro tnum text-ink-muted">
                {formatQty(serverPage)} / {formatQty(totalPages)}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => goToPage(Math.min(totalPages, page + 1))}
              >
                Sonraki
                <ChevronRightIcon aria-hidden />
              </Button>
            </div>
          </div>
        ) : null}
      </Card>

      {/* The colour legend now sits with the tiles above; this keeps the provenance note. */}
      <p className="mt-2 text-micro text-ink-muted">
        Bakiye Korgun'un cari fişi, fatura ve çek kayıtlarından hesaplanır; para birimleri
        birbirine çevrilmez, bu yüzden toplanmaz.
      </p>
    </>
  )
}

/**
 * Receivable / payable per currency.
 *
 * Positive and negative balances are kept apart on purpose: netting them would
 * let one customer's debt cancel another's credit and hide both numbers.
 */
function TotalsStrip({
  totals,
  loading,
}: {
  totals: readonly CariCurrencyTotal[]
  loading: boolean
}) {
  if (loading) {
    return (
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" aria-hidden>
        {[0, 1, 2].map((i) => (
          <Card key={i}>
            <div className="px-3.5 py-3">
              <span className="block h-2.5 w-16 animate-pulse rounded-xs bg-hairline" />
              <span className="mt-2 block h-5 w-32 animate-pulse rounded-xs bg-hairline" />
            </div>
          </Card>
        ))}
      </div>
    )
  }

  if (totals.length === 0) {
    return null
  }

  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {totals.map((t) => (
        <Card key={t.currency}>
          <div className="px-3.5 py-3">
            <p className="eyebrow">{t.currency} bakiye özeti</p>
            <dl className="mt-1.5 flex flex-col gap-1">
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-data text-ink-muted">
                  Toplam alacak
                  <span className="ml-1 font-mono text-micro tnum text-ink-muted/70">
                    ({formatQty(t.accountsReceivable)} cari)
                  </span>
                </dt>
                <dd className="font-mono text-[0.9375rem] tnum font-semibold text-zeytin">
                  {formatAmount(t.receivable, t.currency)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-data text-ink-muted">
                  Toplam borç
                  <span className="ml-1 font-mono text-micro tnum text-ink-muted/70">
                    ({formatQty(t.accountsPayable)} cari)
                  </span>
                </dt>
                <dd className="font-mono text-[0.9375rem] tnum font-semibold text-hardal">
                  {formatAmount(t.payable, t.currency)}
                </dd>
              </div>
            </dl>
          </div>
        </Card>
      ))}
    </div>
  )
}
